import env from "$root/env";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { overrideClassName, propagateRef } from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import {
	type Column,
	type DefaultMenuItem,
	type GetContextMenuItems,
	type GetContextMenuItemsParams,
	type GetMainMenuItemsParams,
	type GridApi,
	type GridReadyEvent,
	type MenuItemDef,
	type SendToClipboardParams,
	AllCommunityModule,
	ModuleRegistry,
	setupAgTestIds,
	themeQuartz,
	type AgEventListener,
	type AgPublicEventType,
} from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import { type AgGridReactProps, AgGridReact } from "ag-grid-react";
import {
	type ReactElement,
	type ReactNode,
	type Ref,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { type ColumnMetadata, spawnColumnChooser } from "$root/ui-lib/table/extra/ColumnChooser";
import "./ag-grid.css";
import { type MaybeArray, noop, stableEmptyArray } from "@mdotm/shadcn/legacy/utils";
import { renderToStaticMarkup } from "react-dom/server";
import { seconds, type Maybe } from "$root/utils";
import { commonColumnDataDefinition } from "$root/ui-lib/table/column/common-data-definitions";
import { groupBy } from "@mdotm/utils/collections";
import { getCurrentUser } from "$root/functional-areas/user";

setupAgTestIds({ testIdAttribute: "data-qualifier" });

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);
LicenseManager.setLicenseKey(env.agGridLibKey);

export const baseGridTheme = themeQuartz.withParams({
	accentColor: "#4cb09cff",
	advancedFilterBuilderColumnPillColor: "#94d0c4ff",
	advancedFilterBuilderJoinPillColor: "#ff777bff",
	advancedFilterBuilderOptionPillColor: "#f69b6fff",
	advancedFilterBuilderValuePillColor: "#66cef5ff",
	// borderColor: "#a5aec080",
	backgroundColor: "#ffffffff",
	chartMenuLabelColor: "#181d1fcc",

	chromeBackgroundColor: "#fafbfbff",
	columnDropCellBackgroundColor: "#181d1f12",
	columnHoverColor: "#b7dfd780",
	dragHandleColor: "#181d1fb3",
	foregroundColor: "#3f485aff",
	findMatchColor: "#ffbe00ff",
	findMatchBackgroundColor: "#ffbe00ff",
	findActiveMatchBackgroundColor: "#ffbe00ff",
	findActiveMatchColor: "#ffbe00ff",
	invalidColor: "#e81e25ff",
	inputDisabledTextColor: "#181d1f80",
	// iconButtonBackgroundColor: "#ffffff00",
	// iconButtonHoverBackgroundColor: "#eaeaebff",
	// iconButtonActiveBackgroundColor: "#3f485a80",
	// iconButtonColor: "#3f485a80",
	inputDisabledBackgroundColor: "#181d1f0f",
	inputPlaceholderTextColor: "#181d1f80",
	iconButtonHoverColor: "#181d1f1a",
	iconButtonActiveColor: "#ffffffff",
	iconButtonActiveIndicatorColor: "#ffffffff",
	menuBackgroundColor: themeCSSVars.palette_N0,
	menuTextColor: themeCSSVars.palette_N700,
	modalOverlayBackgroundColor: "#ffffffa8",
	pickerButtonBackgroundColor: "#ffffffff",
	pickerButtonFocusBackgroundColor: "#ffffffff",
	pickerListBackgroundColor: "#ffffffff",
	rangeSelectionChartBackgroundColor: "#ffffffff",
	rangeSelectionBorderColor: themeCSSVars.palette_S500,
	rangeSelectionBackgroundColor: themeCSSVars.palette_S20,
	rangeSelectionHighlightColor: themeCSSVars.palette_S20,
	rowHoverColor: themeCSSVars.palette_N20,
	selectedRowBackgroundColor: themeCSSVars.palette_N50,

	rangeSelectionChartCategoryBackgroundColor: "#ffffffff",
	rowLoadingSkeletonEffectColor: "#42424233",
	rowNumbersSelectedColor: "#ffffffff",
	rangeHeaderHighlightColor: "#eeeeeeff",
	subtleTextColor: "#181d1f80",
	selectCellBackgroundColor: "#ffffffff",
	sideButtonBackgroundColor: "#ffffff00",
	sideButtonSelectedBackgroundColor: "#ffffff00",
	tabTextColor: "#181d1fb3",
	tabSelectedUnderlineColor: "#ffffff00",
	tabBackgroundColor: "#ffffff00",
	tabBarBackgroundColor: "#f4f4f4ff",
	toggleButtonOffBackgroundColor: "#97999aff",
	valueChangeDeltaUpColor: "#3e8e7eff",
	valueChangeValueHighlightBackgroundColor: "#16a08580",
	valueChangeDeltaDownColor: "#e53935ff",
	fontFamily: "Gotham",
	cellFontFamily: "Gotham",
	headerFontFamily: "Gotham",
	wrapperBorder: false,
	headerBackgroundColor: "transparent",
	dataFontSize: 12,
	headerFontSize: 12,
	headerTextColor: themeCSSVars.palette_N700,
	fontSize: 12,

	checkboxIndeterminateBackgroundColor: "#cacbcbff",
	checkboxIndeterminateBorderColor: "#cacbcbff",
	checkboxUncheckedBorderColor: "#97999aff",
	checkboxBorderRadius: 2,
	checkboxCheckedBorderColor: themeCSSVars.palette_N500,
	checkboxCheckedBackgroundColor: themeCSSVars.palette_N500,
	focusShadow: { color: themeCSSVars.palette_N200, spread: 3 },

	cellEditingBorder: { color: themeCSSVars.palette_N200 },
	inputFocusBorder: { color: themeCSSVars.palette_S500 },
	borderRadius: 2,
	rowGroupIndentSize: 0,
});

export const columnChooserTitleContextKey = "columnChooserTitle";
export const columnChooserSubtitleContextKey = "columnChooserSubtitle";
export const columnChooserMenuItemDef = {
	name: "Choose Columns",
	action(e: { api: GridApi<any>; context?: any }) {
		const cols = e.api.getAllGridColumns() ?? [];
		const groupedByPinnedCols = groupBy(cols, (c) => {
			const pinned = c.getPinned();
			if (pinned === true) {
				return "left";
			}
			if (pinned === false || pinned == null) {
				return "no";
			}
			return pinned;
		});
		const mapFn = (col: Column<any>): ColumnMetadata<string> => ({
			id: col.getColId(),
			label: col.getColDef().headerName ?? col.getColId(),
			checked: col.isVisible(),
			hidden: (col.getColDef().lockPosition ?? false) !== false,
			disabled: col.getColDef().lockVisible ?? false,
		});
		spawnColumnChooser({
			title: e.context?.[columnChooserTitleContextKey],
			description: e.context?.[columnChooserSubtitleContextKey],
			columns: [
				...(groupedByPinnedCols.left?.map(mapFn) ?? []),
				...(groupedByPinnedCols.no?.map(mapFn) ?? []),
				...(groupedByPinnedCols.right?.map(mapFn) ?? []),
			],
			onChange(newColumns) {
				e.api.applyColumnState({
					state: newColumns.map((c) => ({ colId: c.id, hide: !c.checked })),
					applyOrder: true,
				});
			},
		});
	},
	icon: '<span class="ag-icon ag-icon-columns" role="presentation" unselectable="on"></span>',
} satisfies MenuItemDef;

function getMainMenuItemsDefault(params: GetMainMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] {
	return params.defaultItems.map((item) => (item === "columnChooser" ? columnChooserMenuItemDef : item));
}

/**
 * Whether the current user is allowed to export the raw (unformatted) data of any
 * AG Grid table. Driven by the `exportRawAgGridTableWhitelist` runtime-env property
 * (a list of user emails). See `frontend/runtime-envs/_env.schema.json`.
 */
function canExportRawAgGridTable(): boolean {
	// JSON `[]` widens to `never[]`, so normalise to `string[]` for the email comparison.
	const whitelist = (env.exportRawAgGridTableWhitelist ?? []) as readonly string[];
	const email = getCurrentUser().email?.trim().toLowerCase();
	return !!email && whitelist.some((entry) => entry.trim().toLowerCase() === email);
}

/**
 * Builds the "Export raw table" context-menu entry. Exports the underlying cell
 * values (bypassing value formatters via `processCellCallback`) so the output is the
 * raw grid data rather than the display-formatted text.
 */
function rawExportMenuItem(params: GetContextMenuItemsParams<any>): MenuItemDef {
	return {
		name: "Export raw table",
		icon: '<span class="ag-icon ag-icon-save" role="presentation" unselectable="on"></span>',
		subMenu: [
			{
				name: "CSV export (raw)",
				icon: '<span class="ag-icon ag-icon-csv" role="presentation" unselectable="on"></span>',
				action: () => params.api.exportDataAsCsv({ processCellCallback: (p) => p.value }),
			},
			{
				name: "Excel export (raw)",
				icon: '<span class="ag-icon ag-icon-excel" role="presentation" unselectable="on"></span>',
				action: () => params.api.exportDataAsExcel({ processCellCallback: (p) => p.value }),
			},
		],
	} satisfies MenuItemDef;
}

/**
 * Appends the raw-export entry to whatever context menu a grid resolves to (the shared
 * default or a consumer-provided `getContextMenuItems`), gated on the email whitelist.
 * Kept separate so the export item is added globally without clobbering per-grid menus.
 */
function withRawExportMenuItem<TData>(resolveBase: GetContextMenuItems<TData>): GetContextMenuItems<TData> {
	return (params) => {
		const base = resolveBase(params);
		if (!canExportRawAgGridTable()) {
			return base;
		}
		const exportItem = rawExportMenuItem(params);
		return base instanceof Promise
			? base.then((items) => [...items, "separator", exportItem])
			: [...base, "separator", exportItem];
	};
}

const defaultGetContextMenuItems: GetContextMenuItems<any> = (a) =>
	a.node && a.column?.isCellEditable(a.node) ? ["copy", "paste"] : ["copy"];

export const richTextMarker = "\x009a3dc2ed-a648-47d7-9eef-34b6161783e7\x00";

export function richTextClipboardItem({
	html,
	plain,
}: {
	html: {
		/** @default "en-GB" */
		lang?: string;
		style?: string;
		bodyContent: MaybeArray<ReactElement>;
	};
	plain: string;
}): string {
	return (
		// add special marker
		richTextMarker +
		JSON.stringify({
			"text/html": [
				"<!DOCTYPE html>\n",
				renderToStaticMarkup(
					<html lang={html.lang ?? "en-GB"}>
						<head>
							<meta httpEquiv="content-type" content="text/html; charset=utf-8" />
							<meta name="generator" content="MDOTM Sphere" />
							{html.style && (
								<style
									type="text/css"
									// biome-ignore lint/security/noDangerouslySetInnerHtml: we need this in order to fill up the user's clipboard with rich data
									dangerouslySetInnerHTML={{
										__html: html.style,
									}}
								/>
							)}
						</head>
						<body>{html.bodyContent}</body>
					</html>,
				),
			].join("\n"),
			"text/plain": plain,
		})
	);
}

function sendToClipboard({ data }: SendToClipboardParams): void {
	if (data.startsWith(richTextMarker)) {
		const mimeToContent = JSON.parse(data.slice(richTextMarker.length));
		navigator.clipboard.write([new ClipboardItem(mimeToContent)]).catch(noop);
	} else {
		navigator.clipboard
			.write([
				new ClipboardItem({
					"text/plain": data,
				}),
			])
			.catch(noop);
	}
}

export function AgGridWithModules<T>({
	innerRef: innerRefProp,
	...props
}: AgGridReactProps<T> & { innerRef?: Ref<AgGridReact<T>> }): React.JSX.Element {
	const innerRef = useRef<AgGridReact<T> | null>(null);
	// Resolve the consumer-provided menu (or the shared default) and append the raw-export
	// entry on top, so the export is available on every grid regardless of per-grid overrides.
	const propsGetContextMenuItems = props.getContextMenuItems;
	const getContextMenuItems = useCallback<GetContextMenuItems<T>>(
		(params) => withRawExportMenuItem<T>(propsGetContextMenuItems ?? defaultGetContextMenuItems)(params),
		[propsGetContextMenuItems],
	);
	// Must be memoised, not spread inline into the JSX below. AG Grid's React wrapper diffs every
	// prop by identity, and a changed `columnTypes` makes it RECREATE every column definition —
	// which destroys and remounts every header component, detaching whatever is anchored to one
	// (an open column menu then closes itself). A fresh object per render would do that on every
	// render of every grid in the app; keyed on the consumer's own object, a real change still lands.
	const columnTypes = useMemo(
		() => ({
			// Right-aligns the cell content only, leaving the header default-aligned
			// (unlike the built-in "rightAligned" type, which also right-aligns the header).
			rightAlignedCell: { cellClass: "ag-right-aligned-cell" },
			...props.columnTypes,
		}),
		[props.columnTypes],
	);
	const onGridReady = useCallback(
		(e: GridReadyEvent<T>) => {
			props.onGridReady?.(e);
			// Unfortunately, AG Grid propagates the ref before the grid API is ready, and the type lies about it.
			propagateRef(innerRef.current, innerRefProp);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.onGridReady, innerRefProp],
	);
	return (
		<AgGridReact
			theme={baseGridTheme}
			tooltipInteraction
			sendToClipboard={sendToClipboard}
			undoRedoCellEditing
			undoRedoCellEditingLimit={50}
			// Design team doesn't like it. I agree btw
			animateRows={false}
			gridOptions={{
				suppressColumnVirtualisation: true,
				// popupParent: document.body,
				...props.gridOptions,
			}}
			// autoSizeStrategy={{
			// 	type: "fitCellContents",
			// 	skipHeader: false,
			// 	columnLimits: [{ colId: "DESCRIPTION", maxWidth: 250 }],
			// }}
			groupDisplayType="singleColumn"
			dataTypeDefinitions={commonColumnDataDefinition}
			suppressAggFuncInHeader
			getMainMenuItems={getMainMenuItemsDefault}
			tooltipShowDelay={seconds(1)}
			tooltipHideDelay={seconds(2)}
			{...props}
			// Must come after {...props}: this wrapper delegates to the consumer's
			// getContextMenuItems (when provided) and appends the raw-export entry on top.
			getContextMenuItems={getContextMenuItems}
			columnTypes={columnTypes}
			ref={innerRef}
			onGridReady={onGridReady}
			cellSelection={{ handle: { mode: "fill" } }}
		/>
	);
}

export function useAgGridRowListener<K extends AgPublicEventType, TData>(
	grid: Maybe<Pick<AgGridReact<TData>, "api">>,
	name: K,
	callback: AgEventListener<TData, any, K>,
	opts?: { disabled?: boolean } | undefined,
): void {
	const callbackRef = useUnsafeUpdatedRef(callback);
	useLayoutEffect(() => {
		const api = grid?.api;
		if (!api) {
			return;
		}
		if (opts?.disabled) {
			return;
		}
		const wrappedCb: typeof callback = (e) => {
			callbackRef.current(e);
		};
		api.addEventListener(name, wrappedCb);
		return () => api.removeEventListener(name, wrappedCb);
	}, [callbackRef, name, opts?.disabled, grid]);
}

export type AgGridTooltipWrapperProps = StylableProps & { children?: ReactNode };

export function AgGridTooltipWrapper(props: AgGridTooltipWrapperProps): React.JSX.Element {
	return (
		<div
			className={overrideClassName(
				"bg-white rounded-sm border shadow-lg py-2 px-2 overflow-auto max-h-[300px]",
				props.className,
			)}
			style={props.style}
		>
			{props.children}
		</div>
	);
}

export function useAgGridSelectedNodes<TData>(grid: Maybe<Pick<AgGridReact<TData>, "api">>): TData[] {
	const [selectedNodes, setSelectedNodes] = useState<TData[]>(stableEmptyArray);
	useAgGridRowListener(grid, "selectionChanged", ({ api }) => {
		setSelectedNodes(() => api.getSelectedRows());
	});

	return selectedNodes;
}
